import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { VoiceToTextMicButton } from "@/components/ui/VoiceToTextMicButton";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError, apiFetch } from "@/lib/api";
import {
  draftConsumeQuantityFromKeystrokes,
  stepConsumeQuantityDraft,
} from "@/lib/inventoryConsumeQuantity";
import {
  consumeInventoryBoxAll,
  fetchInventoryBoxPendingAdjustment,
  releaseInventoryBoxToFloor,
  type InventoryBox,
} from "@/lib/inventoryBoxApi";
import { cn } from "@/lib/utils";

const CONSUME_QUANTITY_STEPS = [
  ["-10", -10],
  ["-1", -1],
  ["+1", 1],
  ["+10", 10],
] as const;

export function InventoryBoxConsumeCard(props: {
  box: InventoryBox;
  disabled?: boolean;
  onChanged: () => void;
  onPendingChange?: (pending: boolean) => void;
  onCompleted?: (box: InventoryBox) => void;
  onCancel?: () => void;
}): JSX.Element {
  const { showToast } = useHubToast();
  const [reason, setReason] = useState("");
  const [quantityDraft, setQuantityDraft] = useState("");
  const syncKey = useRef("");
  const pendingQuery = useQuery({
    queryKey: ["inventory-box-pending", props.box.hex],
    queryFn: () => fetchInventoryBoxPendingAdjustment(props.box.hex),
    staleTime: 15_000,
  });
  const pendingRequest = pendingQuery.data?.pending_adjustment_request ?? null;
  const savedMpnLabel = props.box.saved_mpn_label?.trim() || null;
  const quantity = quantityDraft.trim() === "" ? 0 : Number.parseInt(quantityDraft, 10);
  const quantityValid = Number.isFinite(quantity) && quantity >= 1 && quantity <= props.box.quantity;

  useEffect(() => {
    const key = `${props.box.hex}:${String(props.box.quantity)}`;
    if (syncKey.current === key) return;
    syncKey.current = key;
    setQuantityDraft("");
  }, [props.box.hex, props.box.quantity]);

  const consumeMutation = useMutation({
    mutationFn: (nextQuantity: number) =>
      consumeInventoryBoxAll(
        props.box.hex,
        reason.trim(),
        nextQuantity,
        props.box.approved_component_id,
      ),
    onSuccess: (result) => {
      props.onChanged();
      setReason("");
      const remaining = result.box.quantity;
      const pending = result.pending_adjustment_request != null;
      showToast({
        message:
          remaining > 0
            ? pending
              ? `Consumed. ${String(remaining)} left on this carton. Megaventory adjustment is pending approval.`
              : `Consumed. ${String(remaining)} left on this carton.`
            : pending
              ? "Container consumed. Megaventory adjustment is pending approval."
              : "Container consumed.",
        tone: "success",
      });
      props.onCompleted?.(result.box);
    },
    onError: (error) => {
      showToast({
        message: error instanceof ApiError ? error.message : String(error),
        tone: "error",
      });
    },
  });

  const onPendingChangeRef = useRef(props.onPendingChange);
  onPendingChangeRef.current = props.onPendingChange;
  useEffect(() => {
    onPendingChangeRef.current?.(consumeMutation.isPending);
    return () => onPendingChangeRef.current?.(false);
  }, [consumeMutation.isPending]);

  const locked = props.disabled === true || consumeMutation.isPending || pendingRequest != null;

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp
          hoverHint="Removes this quantity from the carton now. The carton stays when quantity remains. Megaventory posts after someone approves the adjustment."
        >
          Consume
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-4">
        {savedMpnLabel != null ? (
          <p className="text-sm text-slate-700">
            <span className="font-medium text-slate-600">Manufacturer part number: </span>
            <span className="font-mono">{savedMpnLabel}</span>
          </p>
        ) : null}
        <div>
          <Label htmlFor="inventory-box-consume-quantity">Quantity to consume</Label>
          <Input
            id="inventory-box-consume-quantity"
            className="text-center text-2xl font-semibold tabular-nums"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={quantityDraft}
            disabled={locked}
            onChange={(event) => {
              setQuantityDraft(draftConsumeQuantityFromKeystrokes(event.target.value, props.box.quantity));
            }}
          />
          <div className="mt-2 grid grid-cols-4 gap-2">
            {CONSUME_QUANTITY_STEPS.map(([label, delta]) => (
              <Button
                key={label}
                type="button"
                variant="secondary"
                className="h-12 text-base"
                disabled={locked || props.box.quantity < 1}
                onClick={() => {
                  setQuantityDraft((current) => stepConsumeQuantityDraft(current, delta, props.box.quantity));
                }}
              >
                {label}
              </Button>
            ))}
          </div>
          <Button
            type="button"
            variant="secondary"
            className="mt-2 h-12 w-full text-base"
            disabled={locked || props.box.quantity < 1}
            onClick={() => setQuantityDraft(String(props.box.quantity))}
          >
            Set to max
          </Button>
        </div>
        <div>
          <Label htmlFor="inventory-box-consume-reason">Reason</Label>
          <div className="flex items-stretch gap-2">
            <Input
              id="inventory-box-consume-reason"
              className="text-base"
              type="text"
              autoComplete="off"
              value={reason}
              disabled={locked}
              onChange={(event) => setReason(event.target.value)}
            />
            <VoiceToTextMicButton
              className="h-10 w-10"
              value={reason}
              onValueChange={setReason}
              disabled={locked}
              ariaLabel="Dictate consume reason"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            disabled={reason.trim() === "" || !quantityValid || locked}
            onClick={() => consumeMutation.mutate(quantity)}
          >
            {consumeMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Consume
          </Button>
          {props.onCancel != null ? (
            <Button type="button" variant="secondary" disabled={locked} onClick={props.onCancel}>
              Cancel
            </Button>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}

export function InventoryBoxReleaseToFloorCard(props: {
  box: InventoryBox;
  disabled?: boolean;
  onChanged: () => void;
  onPendingChange?: (pending: boolean) => void;
  onCompleted?: (box: InventoryBox) => void;
  onCancel?: () => void;
  /** Fields only. The parent modal owns Release and Cancel. */
  plain?: boolean;
  onSubmitApi?: (api: { submit: () => void; canSubmit: boolean; pending: boolean }) => void;
}): JSX.Element {
  const { showToast } = useHubToast();
  const [reason, setReason] = useState("");
  const [stationId, setStationId] = useState("");
  const stationsQuery = useQuery({
    queryKey: ["inventory-box", "floor-stations"],
    queryFn: () => apiFetch<{ stations: { id: number; name: string }[] }>("/api/inventory-boxes/floor-stations"),
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
  });
  const releaseMutation = useMutation({
    mutationFn: () => {
      const parsedStationId = Number.parseInt(stationId, 10);
      if (!Number.isFinite(parsedStationId) || parsedStationId <= 0) {
        throw new Error("Pick a station.");
      }
      return releaseInventoryBoxToFloor(props.box.hex, parsedStationId, reason.trim());
    },
    onSuccess: (result) => {
      props.onChanged();
      setReason("");
      setStationId("");
      showToast({ message: "Container released to the floor station.", tone: "success" });
      props.onCompleted?.(result.box);
    },
    onError: (error) => {
      showToast({
        message: error instanceof ApiError ? error.message : String(error),
        tone: "error",
      });
    },
  });

  const onPendingChangeRef = useRef(props.onPendingChange);
  onPendingChangeRef.current = props.onPendingChange;
  useEffect(() => {
    onPendingChangeRef.current?.(releaseMutation.isPending);
    return () => onPendingChangeRef.current?.(false);
  }, [releaseMutation.isPending]);

  const stations = stationsQuery.data?.stations ?? [];
  const locked = props.disabled === true || releaseMutation.isPending;
  const canSubmit = reason.trim() !== "" && stationId.trim() !== "" && !locked;
  const releaseMutateRef = useRef(releaseMutation.mutate);
  releaseMutateRef.current = releaseMutation.mutate;
  const onSubmitApiRef = useRef(props.onSubmitApi);
  onSubmitApiRef.current = props.onSubmitApi;

  useEffect(() => {
    onSubmitApiRef.current?.({
      submit: () => releaseMutateRef.current(),
      canSubmit,
      pending: releaseMutation.isPending,
    });
  }, [canSubmit, releaseMutation.isPending]);

  const fields = (
    <>
        <fieldset className="space-y-2" disabled={locked || stationsQuery.isLoading}>
          <legend className="text-sm font-medium text-slate-700">Station</legend>
          {stationsQuery.isLoading ? (
            <p className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading stations.
            </p>
          ) : stationsQuery.isError ? (
            <p className="text-sm text-amber-800">Could not load stations. Try again in a moment.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Floor station">
              {stations.map((station) => {
                const stationValue = String(station.id);
                const selected = stationId === stationValue;
                return (
                  <button
                    key={station.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={locked}
                    className={cn(
                      "flex min-h-[5.25rem] w-full items-center justify-center rounded-xl border px-2 py-3 text-center text-sm font-semibold leading-snug touch-manipulation",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1",
                      selected
                        ? "border-brand-500 bg-brand-50 text-brand-900 shadow-sm"
                        : "border-slate-200 bg-white text-slate-800 hover:border-brand-300 hover:bg-brand-50/40",
                      locked && "cursor-not-allowed opacity-60",
                    )}
                    onClick={() => setStationId(stationValue)}
                  >
                    {station.name}
                  </button>
                );
              })}
            </div>
          )}
        </fieldset>
        <div>
          <Label htmlFor="inventory-box-release-reason">Reason</Label>
          <div className="flex items-stretch gap-2">
            <Input
              id="inventory-box-release-reason"
              className="text-base"
              type="text"
              autoComplete="off"
              value={reason}
              disabled={locked}
              onChange={(event) => setReason(event.target.value)}
            />
            <VoiceToTextMicButton
              className="h-10 w-10"
              value={reason}
              onValueChange={setReason}
              disabled={locked}
              ariaLabel="Dictate release reason"
            />
          </div>
        </div>
    </>
  );

  if (props.plain === true) {
    return <div className="space-y-4">{fields}</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="Moves this carton off the warehouse shelf and onto a floor station. Warehouse on-hand does not change.">
          Release to floor
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-4">
        {fields}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            disabled={reason.trim() === "" || stationId.trim() === "" || locked}
            onClick={() => releaseMutation.mutate()}
          >
            {releaseMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Release to station
          </Button>
          {props.onCancel != null ? (
            <Button type="button" variant="secondary" disabled={locked} onClick={props.onCancel}>
              Cancel
            </Button>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}
